import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, EMPTY, from, switchMap, throwError } from "rxjs";
import { CognitoUtilsService } from "../services/cognito-utils.service";
import { LogoutEventService } from "../services/logout-event.service";
import { SKIP_AUTH_HEADER } from "../constants/authenticate.constants";

/**
 * Interceptor that attaches the Cognito ID token to outgoing requests.
 *
 * If retrieving the session fails — which happens when the refresh token has
 * expired and Cognito can no longer silently refresh the ID/access token — the
 * session is definitively dead. In that case we trigger a single, deterministic
 * logout (returning the user to the login page) instead of firing the request
 * without a token, which would only produce a 401 and leave the app stuck on a
 * blank page while several redirects race each other.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Fast path: skip auth entirely for requests with SKIP_AUTH_HEADER
  // This avoids calling getSession() for S3/external requests, preventing potential race conditions
  if (req.headers.has(SKIP_AUTH_HEADER)) {
    return next(req.clone({ headers: req.headers.delete(SKIP_AUTH_HEADER) }));
  }

  const cognitoUtilsService = inject(CognitoUtilsService);
  const logoutEventService = inject(LogoutEventService);

  return from(cognitoUtilsService.getSession()).pipe(
    switchMap((session) => {
      const idToken = session?.getIdToken().getJwtToken();

      // No current user (e.g. login/public pages, or already signed out):
      // proceed without an Authorization header.
      if (!idToken) {
        return next(req);
      }

      // Note: the logout dedup guard is reset only on an explicit session
      // transition in AuthService._checkUserSession() (login/bootstrap), not
      // here — resetting on every request could re-arm the guard mid-burst
      // (some in-flight requests still carry a not-yet-expired token) and let
      // a second logout slip through.
      return next(
        req.clone({
          headers: req.headers.append("Authorization", `Bearer ${idToken}`),
        }),
      );
    }),
    catchError((error: unknown) => {
      // Downstream HTTP errors (including 401s) are already handled further
      // along the chain (unauthorizedInterceptor / toastInterceptor); just
      // rethrow so they can surface normally.
      if (error instanceof HttpErrorResponse) {
        return throwError(() => error);
      }

      // getSession() rejected: the refresh token is expired/invalid, so the
      // session is dead. Log out once and abort the request instead of sending
      // it tokenless (which would just 401).
      logoutEventService.emitLogout();
      return EMPTY;
    }),
  );
};
